import { ComponentProps, FC, useCallback, useEffect, useRef, useState } from 'react';

import { planExternalBrowser, redirectAttemptsOf, RedirectPlan } from 'services/browser-service';
import ExternalBrowserPage from './ExternalBrowserPage';

const FALLBACK_TIMEOUT = 1500;
const REDIRECT_TIMEOUT = 2000;

/**
 * @param InComponent
 * @description HOC 외부 브라우저 게이트
 * 카카오 인앱 등 지원하지 않는 브라우저면 크롬(iOS 는 Safari)으로 옮기고, 통과할 때까지 InComponent 를 마운트하지 않는다.
 * InComponent 가 마운트되면 getRoom / decryptPhone / watchPosition 이 바로 시작되므로 그 앞에서 막아야 한다.
 */
export const withExternalBrowser = (InComponent: FC) => {
  // eslint-disable-next-line
  return (props: ComponentProps<any>) => {
    const [plan] = useState<RedirectPlan>(() => planExternalBrowser(window.location.href));
    const [failed, setFailed] = useState(false);
    const leftRef = useRef(false);
    const timersRef = useRef<number[]>([]);

    const clearTimers = () => {
      timersRef.current.forEach((id) => window.clearTimeout(id));
      timersRef.current = [];
    };

    const stillHere = () => !leftRef.current && document.visibilityState === 'visible';

    const runRedirect = useCallback(() => {
      if (plan.type === 'none') {
        return;
      }
      clearTimers();
      leftRef.current = false;
      setFailed(false);

      const attempts = redirectAttemptsOf(plan);
      const tryAt = (index: number) => {
        if (index >= attempts.length || !stillHere()) {
          return;
        }
        window.location.href = attempts[index];
        const isLast = index === attempts.length - 1;
        timersRef.current.push(
          window.setTimeout(
            () => {
              if (!stillHere()) {
                return;
              }
              if (isLast) {
                setFailed(true);
              } else {
                tryAt(index + 1);
              }
            },
            isLast ? REDIRECT_TIMEOUT : FALLBACK_TIMEOUT
          )
        );
      };
      tryAt(0);
    }, [plan]);

    useEffect(() => {
      if (plan.type === 'none') {
        return;
      }
      const onVisibility = () => {
        if (document.visibilityState === 'hidden') {
          leftRef.current = true;
          clearTimers();
        } else if (leftRef.current) {
          setFailed(true);
        }
      };
      const onPageHide = () => {
        leftRef.current = true;
        clearTimers();
      };
      document.addEventListener('visibilitychange', onVisibility);
      window.addEventListener('pagehide', onPageHide);
      runRedirect();

      return () => {
        clearTimers();
        document.removeEventListener('visibilitychange', onVisibility);
        window.removeEventListener('pagehide', onPageHide);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (plan.type === 'none') {
      return <InComponent {...props} />;
    }

    if (plan.type === 'android') {
      return (
        <ExternalBrowserPage
          redirecting={!failed}
          openLabel="크롬으로 열기"
          onOpen={runRedirect}
          storeUrl={plan.storeUrl}
          guide={'버튼이 동작하지 않으면 링크를 복사한 뒤\n크롬 브라우저 주소창에 붙여넣어 주세요.'}
        />
      );
    }

    return (
      <ExternalBrowserPage
        redirecting={!failed}
        openLabel="다른 브라우저로 열기"
        onOpen={runRedirect}
        guide={'버튼이 동작하지 않으면 오른쪽 위 메뉴에서\n"다른 브라우저로 열기"를 눌러 주세요.'}
      />
    );
  };
};
